import React, { useEffect, useState } from "react";
import { Modal, Nav, NavItem, NavLink } from "reactstrap";
import classnames from "classnames";
import moment from "moment";

function joinPublicUrl(pathLike) {
  if (pathLike == null || pathLike === "") return "";
  const p = String(pathLike).trim();
  if (/^https?:\/\//i.test(p) || p.startsWith("data:") || p.startsWith("blob:")) return p;
  const base =
    (process.env.REACT_APP_STORAGE_TYPE === "azure"
      ? process.env.REACT_APP_STORAGE_URL
      : process.env.REACT_APP_API_PUBLIC_URL) || "";
  const rel = p.replace(/^\/+/, "");
  if (!base) return rel ? `/${rel}` : "";
  return `${base.replace(/\/+$/, "")}/${rel}`;
}

const styles = {
  headerTitle: { fontSize: "1.1rem", fontWeight: 600, margin: 0, color: "#1e293b" },
  summaryRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px 12px",
    padding: "12px 0 14px",
    borderBottom: "1px solid #e8ecf1",
    marginBottom: 16,
  },
  metaLine: {
    fontSize: "0.8125rem",
    color: "#64748b",
    marginTop: 4,
  },
  tabNav: { borderBottom: "1px solid #e2e8f0", marginBottom: 4 },
  tabLink: {
    borderRadius: "10px 10px 0 0",
    padding: "10px 16px",
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: "#475569",
    cursor: "pointer",
  },
  tabLinkActive: {
    background: "linear-gradient(180deg, #e0f2fe 0%, #f0f9ff 100%)",
    color: "#0369a1",
    fontWeight: 600,
  },
  tabIcon: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.85rem",
    border: "2px solid #7dd3fc",
    background: "#fff",
    color: "#0369a1",
  },
  tabIconActive: { background: "#0369a1", color: "#fff", borderColor: "#0369a1" },
  sectionTitle: {
    fontSize: "0.78rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "#64748b",
    margin: "20px 0 10px",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  sectionBody: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    padding: "14px 16px",
  },
  kvGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
    gap: "12px 24px",
  },
  kvLabel: { fontSize: "0.72rem", fontWeight: 600, color: "#94a3b8", marginBottom: 2 },
  remarksBox: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "12px 14px",
    minHeight: 48,
    fontSize: "0.9rem",
    color: "#334155",
    whiteSpace: "pre-wrap",
  },
  attachmentThumb: { maxWidth: 120, maxHeight: 120, objectFit: "cover", borderRadius: 8, border: "1px solid #e2e8f0" },
  flightStack: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    lineHeight: 1.35,
  },
  flightMain: { fontWeight: 600, color: "#0f172a", fontSize: "0.95rem" },
  flightSub: { fontSize: "0.82rem", color: "#64748b" },
};

function formatDate(d, fmt = "DD-MMM-YYYY") {
  if (!d) return "—";
  const m = moment(d);
  return m.isValid() ? m.format(fmt) : "—";
}

function Section({ icon, title, children }) {
  return (
    <div>
      <div style={styles.sectionTitle}>
        <i className={`mdi ${icon}`} style={{ fontSize: "1rem", color: "#0369a1" }} />
        {title}
      </div>
      <div style={styles.sectionBody}>{children}</div>
    </div>
  );
}

function FlightBlock({ title, flight, date, iconClass }) {
  const line = flight != null && String(flight).trim() !== "" ? String(flight) : "—";
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
      {iconClass ? (
        <i className={`mdi ${iconClass}`} style={{ fontSize: "1.1rem", color: "#0369a1", marginTop: 20, flexShrink: 0 }} aria-hidden />
      ) : null}
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={styles.kvLabel}>{title}</div>
        <div style={styles.flightStack}>
          <span style={styles.flightMain}>{line}</span>
          <span style={styles.flightSub}>{formatDate(date)}</span>
        </div>
      </div>
    </div>
  );
}

export function TransferBagViewModal({ isOpen, toggle, row: bag }) {
  const [activeTab, setActiveTab] = useState("details");

  useEffect(() => {
    if (isOpen) setActiveTab("details");
  }, [isOpen, bag?.id]);

  if (!bag) return null;

  const notes = Array.isArray(bag.notes) ? bag.notes : [];
  const files = Array.isArray(bag.p_t_m) ? bag.p_t_m : [];
  const t = Number(bag.total_bags ?? 0);
  const r = Number(bag.bags_received ?? 0);
  const c = Number(bag.bags_connected ?? 0);

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" backdrop="static" scrollable id="transfer-bag-view-modal">
      <div className="modal-header" style={{ borderBottom: "none", paddingBottom: 8 }}>
        <h5 style={styles.headerTitle}>Transfer bag</h5>
        <button type="button" className="close" onClick={toggle} aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>

      <div className="modal-body pt-0" style={{ paddingTop: 0 }}>
        <div style={styles.tabNav}>
          <Nav tabs className="nav-tabs-custom">
            <NavItem>
              <NavLink
                href="#"
                tag="a"
                className={classnames({ active: activeTab === "details" })}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab("details");
                }}
                style={{ ...styles.tabLink, ...(activeTab === "details" ? styles.tabLinkActive : {}) }}
              >
                <span style={{ ...styles.tabIcon, ...(activeTab === "details" ? styles.tabIconActive : {}) }}>
                  <i className="mdi mdi-information-outline" />
                </span>
                Details
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                href="#"
                tag="a"
                className={classnames({ active: activeTab === "notes" })}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab("notes");
                }}
                style={{ ...styles.tabLink, ...(activeTab === "notes" ? styles.tabLinkActive : {}) }}
              >
                <span style={{ ...styles.tabIcon, ...(activeTab === "notes" ? styles.tabIconActive : {}) }}>
                  <i className="mdi mdi-history" />
                </span>
                Notes ({notes.length})
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                href="#"
                tag="a"
                className={classnames({ active: activeTab === "files" })}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab("files");
                }}
                style={{ ...styles.tabLink, ...(activeTab === "files" ? styles.tabLinkActive : {}) }}
              >
                <span style={{ ...styles.tabIcon, ...(activeTab === "files" ? styles.tabIconActive : {}) }}>
                  <i className="mdi mdi-file-document-outline" />
                </span>
                Files ({files.length})
              </NavLink>
            </NavItem>
          </Nav>
        </div>

        <div style={styles.summaryRow}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <i className="mdi mdi-bag-suitcase" style={{ fontSize: "1.25rem", color: "#0369a1", marginTop: 2 }} aria-hidden />
            <div style={styles.metaLine}>
              {r}/{t} bags received · {c}/{t} bags connected
            </div>
          </div>
        </div>

        {activeTab === "details" && (
          <div>
            <Section icon="mdi mdi-airplane" title="Flights">
              <div style={styles.kvGrid}>
                <FlightBlock title="From flight" flight={bag.from_flight} date={bag.from_flight_date} iconClass="mdi-airplane-landing" />
                <FlightBlock title="To flight" flight={bag.to_flight} date={bag.to_flight_date} iconClass="mdi-airplane-takeoff" />
              </div>
            </Section>

            <div>
              <div style={styles.sectionTitle}>
                <i className="mdi mdi-comment-text-outline" style={{ fontSize: "1rem", color: "#0369a1" }} />
                Remarks
              </div>
              <div style={styles.remarksBox}>
                {bag.remarks != null && String(bag.remarks).trim() !== "" ? bag.remarks : "—"}
              </div>
            </div>
          </div>
        )}

        {activeTab === "notes" && (
          <div>
            {notes.length === 0 ? (
              <p className="text-muted mb-0">No notes yet.</p>
            ) : (
              notes.map((note, idx) => (
                <div
                  key={note.id ?? idx}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "auto 1fr",
                    gap: "12px 16px",
                    padding: "14px 16px",
                    marginBottom: 10,
                    background: "#f8fafc",
                    borderRadius: 10,
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      color: "#94a3b8",
                      fontSize: "0.85rem",
                      minWidth: 28,
                      textAlign: "center",
                    }}
                  >
                    {notes.length - idx}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.8rem" }}>
                      {note.created_at ? moment(note.created_at).format("Do MMM YYYY | HH:mm") : "—"}
                    </div>
                    <div style={{ fontSize: "0.75rem", color: "#64748b", marginTop: 4 }}>
                      {note.user?.name ?? note.user?.rcno ?? "—"}
                    </div>
                    <div style={{ marginTop: 10, fontSize: "0.9rem", color: "#334155", lineHeight: 1.5 }}>
                      {note.note ?? "—"}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === "files" && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
            {files.length === 0 ? (
              <p className="text-muted mb-0">No PTM files.</p>
            ) : (
              files.map((att, k) => {
                const path = att.path || "";
                const isPdf =
                  path.toLowerCase().endsWith(".pdf") || att.name?.toLowerCase()?.endsWith(".pdf");
                const url = joinPublicUrl(path);
                return (
                  <div key={att.id ?? k} style={{ textAlign: "center", maxWidth: 140 }}>
                    {isPdf ? (
                      <a href={url} target="_blank" rel="noopener noreferrer" className="d-block">
                        <i className="mdi mdi-file-pdf-box" style={{ fontSize: 48, color: "#dc2626" }} />
                      </a>
                    ) : (
                      <a href={url} target="_blank" rel="noopener noreferrer">
                        <img src={url} alt={att.name} style={styles.attachmentThumb} />
                      </a>
                    )}
                    <div style={{ fontSize: "0.75rem", marginTop: 6, wordBreak: "break-all" }}>
                      {att.name || "File"}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      <div className="modal-footer">
        <button type="button" className="btn btn-secondary waves-effect" onClick={toggle}>
          Close
        </button>
      </div>
    </Modal>
  );
}
